/**
 * Available Requests Page
 * Shows requests helpers can apply to
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { requestsAPI, usersAPI } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';
import {
  MapPin,
  Calendar,
  Clock,
  Ship,
  Users,
  Send,
  CheckCircle,
  Filter,
  X,
  Star,
  MessageCircle,
  AlertCircle,
  Anchor,
  Info,
  Globe,
  Briefcase,
  Award,
  UserCheck,
  UserPlus,
  DollarSign,
} from 'lucide-react';
import { format, isPast, parseISO, startOfDay } from 'date-fns';

function AvailableRequests() {
  const navigate = useNavigate();
  const { showToast } = useNotifications();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applicationMessages, setApplicationMessages] = useState({});
  const [applyingId, setApplyingId] = useState(null);
  const [appliedRequests, setAppliedRequests] = useState(new Set());
  const [filter, setFilter] = useState('all');
  const [hoveredRating, setHoveredRating] = useState(null);
  const [dismissingId, setDismissingId] = useState(null);

  // More Info modal state
  const [showMoreInfo, setShowMoreInfo] = useState(null);
  const [moreInfoData, setMoreInfoData] = useState(null);
  const [loadingMoreInfo, setLoadingMoreInfo] = useState(false);

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    try {
      const response = await requestsAPI.getAvailableRequests();
      // API returns { success: true, data: [...] }
      let requestsList = response.data || response.requests || [];

      // Filter out past requests
      requestsList = requestsList.filter(r => {
        const requestDate = parseISO(r.scheduled_date);
        return !isPast(startOfDay(requestDate)) || format(requestDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
      });

      setRequests(requestsList);

      // Track which requests user has already applied to
      const applied = new Set(
        requestsList
          .filter(r => r.has_applied)
          .map(r => r.id)
      );
      setAppliedRequests(applied);
    } catch (error) {
      console.error('Failed to load requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async (requestId) => {
    setApplyingId(requestId);
    try {
      const message = applicationMessages[requestId] || '';
      await requestsAPI.applyToRequest(requestId, message);
      showToast('Application sent successfully!', 'success');
      setAppliedRequests(prev => new Set([...prev, requestId]));
      setApplicationMessages(prev => {
        const updated = { ...prev };
        delete updated[requestId];
        return updated;
      });
    } catch (error) {
      showToast(error.message || 'Failed to apply', 'error');
    } finally {
      setApplyingId(null);
    }
  };

  const handleDismiss = async (requestId) => {
    setDismissingId(requestId);
    try {
      await requestsAPI.dismissRequest(requestId);
      // Remove from list immediately
      setRequests(prev => prev.filter(r => r.id !== requestId));
      showToast('Request hidden', 'success');
    } catch (error) {
      showToast(error.message || 'Failed to dismiss', 'error');
    } finally {
      setDismissingId(null);
    }
  };

  const handleShowMoreInfo = async (userId) => {
    setShowMoreInfo(userId);
    setLoadingMoreInfo(true);
    try {
      const response = await usersAPI.getPublicProfile(userId);
      setMoreInfoData(response.data);
    } catch (error) {
      showToast('Failed to load profile', 'error');
      setShowMoreInfo(null);
    } finally {
      setLoadingMoreInfo(false);
    }
  };

  const filteredRequests = requests.filter(request => {
    if (filter === 'all') return true;
    if (filter === 'urgent') return request.request_type === 'real_time';
    if (filter === 'scheduled') return request.request_type === 'scheduled';
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-display font-bold text-navy-900">
          Available Requests
        </h1>
        <p className="text-navy-600 mt-1">
          Browse and apply to jobs and crew positions in your area
        </p>
      </div>

      {/* Filters */}
      <div className="flex items-center space-x-2">
        <Filter className="h-4 w-4 text-navy-400" />
        {['all', 'urgent', 'scheduled'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors capitalize ${
              filter === f
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-navy-600 hover:bg-gray-200'
            }`}
          >
            {f === 'all' ? 'All Requests' : f}
          </button>
        ))}
      </div>

      {/* Requests List */}
      {filteredRequests.length === 0 ? (
        <div className="card text-center py-12">
          <MapPin className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-navy-900 mb-2">
            No requests available
          </h3>
          <p className="text-navy-600">
            Check back soon or make sure you have service areas set up
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredRequests.map((request) => (
            <div key={request.id} className={`card ${request.application_status === 'selected' ? 'ring-2 ring-gold-400 bg-gold-50/30' : ''}`}>
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 flex-wrap gap-y-2">
                    {request.request_category === 'position' ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gold-100 text-gold-800">
                        <UserPlus className="h-3.5 w-3.5 mr-1" />
                        Crew
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800">
                        <Anchor className="h-3.5 w-3.5 mr-1" />
                        Mooring
                      </span>
                    )}
                    <h3 className="font-medium text-navy-900 text-lg">
                      {request.request_category === 'position' && request.crew_position
                        ? request.crew_position.replace(/_/g, ' ').replace(/^(deck|interior|galley|eng)\s/i, '').replace(/\b\w/g, l => l.toUpperCase())
                        : request.port_name}
                    </h3>
                    {request.request_category === 'position' ? (
                      <span className="badge bg-gold-100 text-gold-800">
                        {request.contract_type === 'permanent' ? 'Full Time' :
                         request.contract_type === 'seasonal' ? 'Seasonal' :
                         request.contract_type === 'temporary' ? 'Daywork' :
                         request.contract_type === 'delivery' ? 'Delivery' : 'Contract'}
                      </span>
                    ) : (
                      <span className={`badge ${
                        request.request_type === 'real_time'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {request.request_type === 'real_time' ? 'Urgent' : 'Scheduled'}
                      </span>
                    )}
                  </div>

                  {/* Key Info for Position Requests - Dates, Port, Salary at top */}
                  {request.request_category === 'position' && (
                    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                      <div className="flex items-center text-navy-700">
                        <Calendar className="h-4 w-4 mr-1.5 text-navy-400" />
                        {format(new Date(request.start_date || request.scheduled_date), 'MMM d')} - {request.end_date ? format(new Date(request.end_date), 'MMM d, yyyy') : 'Ongoing'}
                      </div>
                      <div className="flex items-center text-navy-700">
                        <MapPin className="h-4 w-4 mr-1.5 text-navy-400" />
                        {request.port_name}{request.port_country ? `, ${request.port_country}` : ''}
                      </div>
                      {request.salary && (
                        <div className="flex items-center font-semibold text-green-600">
                          {request.salary_currency === 'EUR' ? '€' : '$'}{request.salary}/mo
                        </div>
                      )}
                    </div>
                  )}

                  {/* Yacht Info */}
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center text-navy-900 font-medium">
                        <Ship className="h-4 w-4 mr-2 text-primary-600" />
                        {request.yacht_name || 'Yacht'}
                        {(request.length_m || request.yacht_length) && (
                          <span className="ml-2 text-sm text-navy-500 font-normal">
                            {request.length_m || request.yacht_length}m
                          </span>
                        )}
                        {request.yacht_type && (
                          <span className="ml-1 px-2 py-0.5 bg-primary-100 text-primary-700 text-xs rounded-full capitalize">
                            {request.yacht_type}
                          </span>
                        )}
                      </div>
                      {/* Owner Rating with Tooltip and More Info button */}
                      <div className="flex items-center space-x-2">
                        <div
                          className="relative"
                          onMouseEnter={() => setHoveredRating(request.id)}
                          onMouseLeave={() => setHoveredRating(null)}
                        >
                          {request.owner_rating > 0 ? (
                            <div className="flex items-center text-sm cursor-pointer">
                              <Star className="h-4 w-4 text-gold-500 fill-current mr-1" />
                              <span className="font-medium text-navy-900">
                                {request.owner_rating.toFixed(1)}
                              </span>
                              <span className="text-navy-500 ml-1">
                                ({request.owner_rating_count})
                              </span>
                            </div>
                          ) : (
                            <div className="flex items-center text-sm text-navy-400">
                              <Star className="h-4 w-4 mr-1" />
                              <span>New captain</span>
                            </div>
                          )}

                          {/* Reviews Tooltip */}
                          {hoveredRating === request.id && request.owner_reviews && request.owner_reviews.length > 0 && (
                            <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-lg shadow-xl border border-gray-200 p-3 z-50">
                              <p className="text-xs font-semibold text-navy-700 mb-2">Recent reviews from helpers:</p>
                              <div className="space-y-2">
                                {request.owner_reviews.map((review, idx) => (
                                  <div key={idx} className="text-xs border-b border-gray-100 pb-2 last:border-0 last:pb-0">
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="font-medium text-navy-800">
                                        {review.reviewer_first_name} {review.reviewer_last_name?.[0]}.
                                      </span>
                                      <div className="flex items-center">
                                        <Star className="h-3 w-3 text-gold-500 fill-current" />
                                        <span className="ml-0.5 text-navy-600">{review.rating}</span>
                                      </div>
                                    </div>
                                    <p className="text-navy-600 italic">"{review.comment}"</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        {/* More Info button */}
                        <button
                          onClick={() => handleShowMoreInfo(request.owner_id)}
                          className="flex items-center text-xs text-primary-600 hover:text-primary-700 bg-primary-50 px-2 py-1 rounded-lg"
                        >
                          <Info className="h-3 w-3 mr-1" />
                          More Info
                        </button>
                      </div>
                    </div>

                    {/* Date, Time, Helpers needed - only for mooring requests */}
                    {request.request_category !== 'position' && (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm text-navy-600">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-1.5" />
                          {format(new Date(request.scheduled_date), 'MMM d, yyyy')}
                        </div>
                        <div className="flex items-center">
                          <Clock className="h-4 w-4 mr-1.5" />
                          {request.scheduled_time || request.eta_from}
                        </div>
                        <div className="flex items-center">
                          <Users className="h-4 w-4 mr-1.5" />
                          {(request.helpers_needed - (request.helpers_confirmed || 0))} position{(request.helpers_needed - (request.helpers_confirmed || 0)) > 1 ? 's' : ''} available
                        </div>
                      </div>
                    )}

                    {/* Mooring type - only for mooring requests */}
                    {request.request_category !== 'position' && request.mooring_type && (
                      <div className="mt-2 flex items-center text-sm text-navy-500">
                        <Anchor className="h-4 w-4 mr-1.5" />
                        Mooring: <span className="capitalize ml-1">{request.mooring_type.replace('_', ' ')}</span>
                      </div>
                    )}

                    {/* Position details - only for position requests */}
                    {request.request_category === 'position' && (
                      <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-navy-500">
                        {request.crew_department && (
                          <div className="flex items-center">
                            <Briefcase className="h-4 w-4 mr-1" />
                            <span className="capitalize">{request.crew_department}</span>
                          </div>
                        )}
                        <div className="flex items-center">
                          <Clock className="h-4 w-4 mr-1" />
                          {request.contract_type === 'permanent' ? 'Full Time' :
                           request.contract_type === 'seasonal' ? 'Seasonal' :
                           request.contract_type === 'temporary' ? 'Daywork' :
                           request.contract_type === 'delivery' ? 'Delivery' :
                           'Temporary'}
                        </div>
                      </div>
                    )}

                    {/* Required Certifications - only for position requests */}
                    {request.request_category === 'position' && (() => {
                      const certs = typeof request.required_certifications === 'string'
                        ? JSON.parse(request.required_certifications || '[]')
                        : (request.required_certifications || []);
                      return certs.length > 0 ? (
                        <div className="mt-2">
                          <div className="flex items-center text-xs text-navy-500 mb-1">
                            <Award className="h-3.5 w-3.5 mr-1" />
                            Required Certifications:
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {certs.map((cert, idx) => (
                              <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                {cert.replace(/_/g, ' ').toUpperCase()}
                              </span>
                            ))}
                          </div>
                        </div>
                      ) : null;
                    })()}

                    {/* Required Languages - only for position requests */}
                    {request.request_category === 'position' && (() => {
                      const langs = typeof request.required_languages === 'string'
                        ? JSON.parse(request.required_languages || '[]')
                        : (request.required_languages || []);
                      return langs.length > 0 ? (
                        <div className="mt-2">
                          <div className="flex items-center text-xs text-navy-500 mb-1">
                            <Globe className="h-3.5 w-3.5 mr-1" />
                            Required Languages:
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {langs.map((lang, idx) => (
                              <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                {lang}
                              </span>
                            ))}
                          </div>
                        </div>
                      ) : null;
                    })()}

                    {/* Minimum Experience - only for position requests */}
                    {request.request_category === 'position' && request.min_experience_years > 0 && (
                      <div className="mt-2 flex items-center text-xs text-navy-500">
                        <Briefcase className="h-3.5 w-3.5 mr-1" />
                        Minimum Experience: <span className="font-medium text-navy-700 ml-1">{request.min_experience_years} year{request.min_experience_years > 1 ? 's' : ''}</span>
                      </div>
                    )}
                  </div>

                  {/* Special Notes / Job Requirements */}
                  {(request.special_notes || request.notes) && (
                    <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start">
                        <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 mr-2 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-amber-800">Job Requirements</p>
                          <p className="text-sm text-amber-700 mt-1">
                            {request.special_notes || request.notes}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-4 sm:mt-0 sm:ml-4 flex flex-col space-y-2 items-end">
                  {request.application_status === 'selected' ? (
                    <>
                      <span className="inline-flex items-center text-gold-600 font-semibold bg-gold-50 px-3 py-1 rounded-full">
                        <Star className="h-4 w-4 mr-1 fill-current" />
                        Selected!
                      </span>
                      <button
                        onClick={() => navigate(`/helper/requests/${request.id}`)}
                        className="btn-primary text-sm relative"
                      >
                        <MessageCircle className="h-4 w-4 mr-1" />
                        View & Chat
                        {request.unread_messages > 0 && (
                          <span className="absolute -top-2 -right-2 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                            {request.unread_messages > 9 ? '9+' : request.unread_messages}
                          </span>
                        )}
                      </button>
                    </>
                  ) : appliedRequests.has(request.id) || request.has_applied ? (
                    <>
                      <span className="inline-flex items-center text-green-600 font-medium">
                        <CheckCircle className="h-5 w-5 mr-1" />
                        Applied
                      </span>
                      <button
                        onClick={() => navigate(`/helper/requests/${request.id}`)}
                        className="btn-secondary text-sm relative"
                      >
                        View & Chat
                        {request.unread_messages > 0 && (
                          <span className="absolute -top-2 -right-2 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                            {request.unread_messages > 9 ? '9+' : request.unread_messages}
                          </span>
                        )}
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => navigate(`/helper/requests/${request.id}`)}
                      className="btn-secondary text-sm"
                    >
                      <Info className="h-4 w-4 mr-1" />
                      View Details
                    </button>
                  )}
                </div>
              </div>

              {/* Application Section - one step process */}
              {!request.has_applied && !appliedRequests.has(request.id) && request.application_status !== 'selected' && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="flex-1">
                      <textarea
                        value={applicationMessages[request.id] || ''}
                        onChange={(e) => setApplicationMessages(prev => ({ ...prev, [request.id]: e.target.value }))}
                        placeholder="Introduce yourself to the captain/owner (optional)"
                        className="input-field text-sm"
                        rows={2}
                      />
                    </div>
                    <div className="flex sm:flex-col gap-2 sm:justify-between">
                      <button
                        onClick={() => handleApply(request.id)}
                        disabled={applyingId === request.id}
                        className="btn-primary flex-1 sm:flex-none"
                      >
                        {applyingId === request.id ? (
                          <span className="flex items-center">
                            <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                            Sending...
                          </span>
                        ) : (
                          <>
                            <Send className="h-4 w-4 mr-2" />
                            Apply Now
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => handleDismiss(request.id)}
                        disabled={dismissingId === request.id}
                        className="text-sm text-gray-400 hover:text-red-500 transition-colors"
                      >
                        Not interested
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* More Info Modal */}
      {showMoreInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-lg w-full shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-display font-semibold text-navy-900">
                Owner Profile
              </h2>
              <button
                onClick={() => { setShowMoreInfo(null); setMoreInfoData(null); }}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {loadingMoreInfo ? (
              <div className="flex items-center justify-center py-12">
                <div className="spinner w-8 h-8" />
              </div>
            ) : moreInfoData ? (
              <div className="space-y-4">
                {/* Basic Info */}
                <div className="flex items-center">
                  <div className="h-16 w-16 bg-primary-100 rounded-full flex items-center justify-center">
                    {moreInfoData.profile_photo_url ? (
                      <img src={moreInfoData.profile_photo_url} alt="" className="h-16 w-16 rounded-full object-cover" />
                    ) : (
                      <span className="text-primary-700 font-semibold text-xl">
                        {moreInfoData.first_name?.[0]}{moreInfoData.last_name?.[0]}
                      </span>
                    )}
                  </div>
                  <div className="ml-4">
                    <p className="text-lg font-medium text-navy-900">
                      {moreInfoData.first_name} {moreInfoData.last_name}
                    </p>
                    <div className="flex items-center text-sm text-navy-600 mt-1">
                      {moreInfoData.rating > 0 ? (
                        <>
                          <Star className="h-4 w-4 text-gold-500 fill-current mr-1" />
                          <span>{moreInfoData.rating.toFixed(1)}</span>
                          <span className="text-navy-400 ml-1">({moreInfoData.rating_count} reviews)</span>
                        </>
                      ) : (
                        <span className="text-navy-400">No ratings yet</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <Briefcase className="h-5 w-5 text-primary-600 mx-auto mb-1" />
                    <p className="text-lg font-semibold text-navy-900">{moreInfoData.completed_jobs || 0}</p>
                    <p className="text-xs text-navy-500">Completed Jobs</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <UserCheck className="h-5 w-5 text-primary-600 mx-auto mb-1" />
                    <p className="text-lg font-semibold text-navy-900">{moreInfoData.months_active || 0}</p>
                    <p className="text-xs text-navy-500">Months Active</p>
                  </div>
                </div>

                {/* Yachts */}
                {moreInfoData.yachts && moreInfoData.yachts.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-navy-700 mb-2 flex items-center">
                      <Ship className="h-4 w-4 mr-1" />
                      Yachts
                    </h3>
                    <div className="space-y-2">
                      {moreInfoData.yachts.map((yacht, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                          <span className="font-medium text-navy-900">{yacht.name}</span>
                          <span className="text-sm text-navy-500">
                            {yacht.yacht_type && <span className="capitalize">{yacht.yacht_type}</span>}
                            {yacht.length_m && <span className="ml-1">· {yacht.length_m}m</span>}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* References */}
                {moreInfoData.references && moreInfoData.references.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-navy-700 mb-2 flex items-center">
                      <Award className="h-4 w-4 mr-1" />
                      Recent Reviews
                    </h3>
                    <div className="space-y-2">
                      {moreInfoData.references.map((ref, idx) => (
                        <div key={idx} className="bg-gray-50 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-navy-800">{ref.from_name}</span>
                            <div className="flex items-center">
                              <Star className="h-3 w-3 text-gold-500 fill-current" />
                              <span className="ml-0.5 text-sm text-navy-600">{ref.rating}</span>
                            </div>
                          </div>
                          <p className="text-sm text-navy-600 italic">"{ref.comment}"</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Member Since */}
                <div className="text-center text-sm text-navy-500 pt-2 border-t border-gray-100">
                  Member since {format(new Date(moreInfoData.member_since), 'MMMM yyyy')}
                </div>
              </div>
            ) : (
              <p className="text-center text-navy-500 py-8">Failed to load profile</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AvailableRequests;
